import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight } from "lucide-react";
import { ChapterHeading } from "./ChapterHeading";
import { getIcon, FILTER_PILLS } from "../lib/concepts";
import { useLang } from "../lib/i18n";

export const Library = ({ concepts, onOpen }) => {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const { t, tf, sectorName } = useLang();
  const pillLabels = t("pills");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return concepts.filter((c) => {
      const inBucket =
        filter === "All" ? true :
        filter === "Confirmed Portfolio" ? c.confirmed :
        c.bucket === filter;
      if (!inBucket) return false;
      if (!q) return true;
      return [c.sector, c.title, c.promise].join(" ").toLowerCase().includes(q);
    });
  }, [concepts, filter, query]);

  return (
    <section id="library" data-testid="library-section" className="bg-ink py-28 md:py-40 grain relative">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
        <ChapterHeading number="02" title={t("library.title")} dark />

        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10 mb-12">
          <div data-testid="filter-pills" className="flex flex-wrap gap-2">
            {FILTER_PILLS.map((p, i) => (
              <button key={p} data-testid={`filter-pill-${p.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(p)}
                className={`text-[10px] tracking-[0.25em] uppercase px-4 py-2.5 border transition-colors duration-300 ${
                  filter === p ? "bg-gold text-ink border-gold" : "border-gold/30 text-cream/60 hover:text-gold hover:border-gold"
                }`}>
                {pillLabels[i] || p}
              </button>
            ))}
          </div>
          <div className="relative lg:ms-auto w-full lg:w-80">
            <Search size={15} className="absolute start-4 top-1/2 -translate-y-1/2 text-gold/60" />
            <input data-testid="library-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t("library.search")} aria-label="Search concepts"
              className="w-full bg-transparent border border-gold/30 focus:border-gold outline-none ps-11 pe-4 py-3 text-sm text-cream placeholder:text-cream/35 transition-colors duration-300" />
          </div>
        </div>

        <p data-testid="library-count" className="text-cream/40 text-[11px] tracking-[0.3em] uppercase mb-8">
          {tf("library.showing", { n: visible.length, t: concepts.length })}
        </p>

        <motion.div layout data-testid="library-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gold/20 border border-gold/20">
          <AnimatePresence mode="popLayout">
            {visible.map((c) => {
              const Icon = getIcon(c.icon);
              return (
                <motion.button
                  layout key={c.ref}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  data-testid={`concept-card-${c.ref.toLowerCase()}`}
                  onClick={() => onOpen(c)}
                  className="group relative bg-ink text-start p-7 md:p-8 flex flex-col min-h-[260px] hover:bg-green/60 transition-colors duration-400 cursor-pointer">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/70">{c.ref}</span>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-cream/40">{sectorName(c.sector)}</span>
                  </div>
                  {c.confirmed && (
                    <span data-testid={`confirmed-badge-${c.ref.toLowerCase()}`}
                      className="absolute top-7 end-7 mt-6 text-[9px] tracking-[0.25em] uppercase bg-gold text-ink px-2 py-0.5">
                      {t("library.confirmed")}
                    </span>
                  )}
                  <Icon size={26} strokeWidth={1.25} className="text-gold mb-5 transition-transform duration-500 group-hover:-translate-y-1" />
                  <h3 className="font-display text-xl md:text-2xl text-cream leading-snug">{c.title}</h3>
                  <p className="mt-3 text-sm text-cream/55 leading-relaxed flex-1">{c.promise}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold/70 group-hover:text-gold transition-colors duration-300">
                    {t("library.open")} <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1" />
                  </span>
                </motion.button>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {visible.length === 0 && (
          <p data-testid="library-empty" className="mt-10 font-display italic text-cream/40 text-xl">{t("library.empty")}</p>
        )}
      </div>
    </section>
  );
};
